import { useCallback, useEffect, useMemo, useState } from 'react';
import { ShieldWarning, Warning, Eye, Robot } from '@phosphor-icons/react';

import Header from '../components/header';
import { HeaderContent, HEADER_TITLE_CLASS, HEADER_BADGE_CLASS } from '../components/shell/app-header';
import { Button } from '@/components/ui/button';
import { NativeSelect } from '@/components/ui/select';
import { useNavigate } from '../lib/routing';
import { useUrlPagination } from '../hooks/use-url-pagination';
import {
  CATEGORY_LABELS,
  SEVERITY_LABELS,
  SOURCE_LABELS,
  STATUS_LABELS,
  fetchAlerts,
  fetchSafeguardingHealth,
  fmtDateTime,
  healthVerdict,
  type SafeguardingHealth,
  type SafeguardingAlertFilter,
  type SafeguardingAlertRow,
} from '../lib/admin-safeguarding';

const HEADER_ICON = <ShieldWarning size={26} />;
const PAGE_SIZE = 25;

function Kpi({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: React.ReactNode; sub?: string }) {
  return (
    <div className="flex flex-col gap-1 rounded-[var(--naia-r-card)] border border-[var(--naia-line)] bg-[var(--naia-card)] p-4 shadow-[var(--naia-shadow-card)]">
      <div className="flex items-center gap-2 text-[var(--naia-text-secondary)]">
        <span className="text-[var(--naia-blue)]">{icon}</span>
        <span className="text-[11px] font-black uppercase tracking-[0.7px]">{label}</span>
      </div>
      <div className="text-[22px] font-extrabold leading-tight text-[var(--naia-text-primary)]">{value}</div>
      {sub && <div className="text-[11.5px] text-[var(--naia-text-secondary)]">{sub}</div>}
    </div>
  );
}

function SeverityBadge({ severity }: { severity: string }) {
  const danger = severity === 'person_at_risk';
  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-black uppercase tracking-[0.5px] ${
        danger
          ? 'bg-[var(--naia-error-bg)] text-[var(--naia-error)]'
          : 'bg-[var(--naia-info-bg)] text-[var(--naia-blue)]'
      }`}
    >
      {danger && <Warning size={13} weight="fill" />}
      {SEVERITY_LABELS[severity]?.label ?? severity}
    </span>
  );
}

export default function AdminSafeguarding() {
  const navigate = useNavigate();
  const { page, setPage, totalPages } = useUrlPagination();

  const [items, setItems] = useState<SafeguardingAlertRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [health, setHealth] = useState<SafeguardingHealth | null>(null);

  const [severity, setSeverity] = useState('');
  const [status, setStatus] = useState('open');

  const filter = useMemo<SafeguardingAlertFilter>(
    () => ({
      severity: (severity || undefined) as SafeguardingAlertFilter['severity'],
      status: (status || undefined) as SafeguardingAlertFilter['status'],
    }),
    [severity, status],
  );

  const load = useCallback(
    (f: SafeguardingAlertFilter, p: number) => {
      setLoading(true);
      setError(null);
      fetchAlerts({ ...f, page: p, limit: PAGE_SIZE })
        .then((r) => {
          setItems([...r.items]);
          setTotal(r.total);
        })
        .catch((e) => setError(e instanceof Error ? e.message : String(e)))
        .finally(() => setLoading(false));
    },
    [],
  );

  useEffect(() => {
    load(filter, page);
  }, [filter, page, load]);

  useEffect(() => {
    fetchSafeguardingHealth().then(setHealth).catch(() => setHealth(null));
  }, []);

  const pages = totalPages(total, PAGE_SIZE);

  const danger = items.filter((a) => a.severity === 'person_at_risk').length;
  const missed = items.filter((a) => a.modelMissed).length;

  return (
    <>
      <Header />
      <main className="mx-auto max-w-[1100px] px-5 pb-16 pt-2">
        <HeaderContent>
          <span className={HEADER_BADGE_CLASS}>{HEADER_ICON}</span>
          <h1 className={HEADER_TITLE_CLASS}>Alertes</h1>
        </HeaderContent>

        {(() => {
          const v = healthVerdict(health);
          return (
            <div
              className={`mt-3 flex items-start gap-2 rounded-[var(--naia-r-card)] border border-[var(--naia-line)] p-3 text-[13px] ${
                v.ok
                  ? 'bg-[var(--naia-info-bg)] text-[var(--naia-text-primary)]'
                  : 'bg-[var(--naia-error-bg)] text-[var(--naia-error)]'
              }`}
            >
              {v.ok ? <ShieldWarning size={18} /> : <Warning size={18} weight="fill" />}
              <span>
                {v.label}
                {!v.ok && (
                  <strong className="block font-extrabold">
                    Une liste vide ci-dessous ne signifie PAS qu&apos;aucune personne n&apos;est en danger.
                  </strong>
                )}
              </span>
            </div>
          );
        })()}

        <p className="mt-3 max-w-[70ch] text-[13px] leading-relaxed text-[var(--naia-text-secondary)]">
          Le dispositif alerte des humains ; il ne décide jamais et ne signale rien à l&apos;extérieur.
          La décision d&apos;agir vous appartient. La conduite à tenir est décrite dans{' '}
          <code className="rounded bg-[var(--naia-info-bg)] px-1 py-0.5 text-[12px]">docs/safeguarding-alerts.md</code>.
          Toute ouverture de dossier est tracée.
        </p>

        <div className="mt-5 grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3">
          <Kpi icon={<Warning size={18} />} label="Personnes en danger" value={danger} sub="sur la page courante" />
          <Kpi icon={<ShieldWarning size={18} />} label="Total" value={total} sub="selon les filtres" />
          <Kpi
            icon={<Robot size={18} />}
            label="Non vues par le modèle"
            value={missed}
            sub="levées par le seul contrôle serveur"
          />
        </div>

        <div className="mt-5 flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-black uppercase tracking-[0.7px] text-[var(--naia-text-secondary)]">
              Gravité
            </span>
            <NativeSelect
              value={severity}
              onChange={(e) => {
                setSeverity(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Toutes</option>
              <option value="person_at_risk">Personne en danger</option>
              <option value="legal_risk">Risque légal</option>
            </NativeSelect>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-black uppercase tracking-[0.7px] text-[var(--naia-text-secondary)]">
              État
            </span>
            <NativeSelect
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
            >
              <option value="open">À traiter</option>
              <option value="in_review">En cours</option>
              <option value="closed_no_action">Clos — sans suite</option>
              <option value="closed_actioned">Clos — suite donnée</option>
              <option value="">Tous</option>
            </NativeSelect>
          </label>
        </div>

        {error && (
          <div className="mt-4 rounded-[var(--naia-r-card)] border border-[var(--naia-line)] bg-[var(--naia-error-bg)] p-4 text-[13px] text-[var(--naia-error)]">
            {error}
          </div>
        )}

        <div className="mt-4 overflow-x-auto rounded-[var(--naia-r-card)] border border-[var(--naia-line)] bg-[var(--naia-card)] shadow-[var(--naia-shadow-card)]">
          <table className="w-full min-w-[720px] border-collapse text-[13px]">
            <thead>
              <tr className="border-b border-[var(--naia-line)] text-left text-[11px] font-black uppercase tracking-[0.7px] text-[var(--naia-text-secondary)]">
                <th className="px-4 py-3">Détecté le</th>
                <th className="px-4 py-3">Gravité</th>
                <th className="px-4 py-3">Nature</th>
                <th className="px-4 py-3">Origine</th>
                <th className="px-4 py-3">État</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-[var(--naia-text-secondary)]">
                    Chargement…
                  </td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-[var(--naia-text-secondary)]">
                    Aucune alerte pour ces filtres.
                  </td>
                </tr>
              )}
              {!loading &&
                items.map((a) => (
                  <tr key={a.id} className="border-b border-[var(--naia-line)] last:border-0">
                    <td className="whitespace-nowrap px-4 py-3 text-[var(--naia-text-primary)]">
                      {fmtDateTime(a.detectedAt)}
                      {a.severity === 'person_at_risk' && !a.notifiedAt && (
                        <div className="text-[11px] font-bold text-[var(--naia-error)]">non notifiée</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <SeverityBadge severity={a.severity} />
                    </td>
                    <td className="px-4 py-3 text-[var(--naia-text-primary)]">
                      {CATEGORY_LABELS[a.category] ?? a.category}
                    </td>
                    <td className="px-4 py-3 text-[var(--naia-text-secondary)]">
                      {SOURCE_LABELS[a.source] ?? a.source}
                      {a.modelMissed && (
                        <div className="text-[11px] font-bold text-[var(--naia-text-secondary)]">
                          modèle silencieux
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[var(--naia-text-secondary)]">
                      {STATUS_LABELS[a.status] ?? a.status}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="outline" size="sm" onClick={() => navigate(`/admin/safeguarding/${a.id}`)}>
                        <Eye size={15} /> Ouvrir
                      </Button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {pages > 1 && (
          <div className="mt-4 flex items-center justify-center gap-3">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              Précédent
            </Button>
            <span className="text-[12px] text-[var(--naia-text-secondary)]">
              Page {page} / {pages}
            </span>
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>
              Suivant
            </Button>
          </div>
        )}

        <p className="mt-3 text-[12px] text-[var(--naia-text-secondary)]">
          Ouvrir un dossier enregistre votre nom et l&apos;heure de consultation.
        </p>
      </main>
    </>
  );
}
